import { cx } from '@linaria/core';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import { userPreferencesQuery } from '@pm2/api';
import { getAllCoursesQuery } from '@pm2/course-builder';
import { useAppContext } from '@provider/app-context';
import { useLocation } from '@provider/navigation';
import { useUserIdentityState } from '@provider/user-identity';
import { ResultsIcon, CoursesIcon, PortalIcon, TeamsIcon, ReadOutLoudIcon, FontIcon, EkgIcon } from '@ui/kit';
import { throttleToAnimationFrame, prefetchQueryIfNeeded } from '@utils/common';
import { Routes } from '../../const.routes.js';
import { isNotificationConsideredRecent } from '../../features/notifications/util.is-considered-recent.js';
import { getPortalsForUserQuery } from '../../features/portals/api.get-for-user.js';
import { preloadPortalImages } from '../../features/portals/util.preload-portal-images.js';
import { getTeamsQuery } from '../../features/settings/molecule.my-teams/api.get-teams.query.js';
import { getStatusForProductQuery } from '../../features/status/api.get-status.js';
import { Group } from './atom.group.js';
import { InternalLink } from './atom.link.js';
import { User } from './atom.user.js';
import { listCls, leftNavCls } from './root.left-nav.style.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useComponentEntrySelector } from '../../features/component-entries/state.js';
import { useGetNotificationsQuery, getNotificationsQuery } from '../../features/notifications/api.get-notifications.query.js';
import { getComponentEntriesQuery } from '../../features/component-entries/api/query.get-component-entries.js';

const userAreaNavSelector = `nav[data-userarea-nav]`;
function LeftNav() {
  const navRef = useRef(null);
  const location = useLocation();
  useRoundedHeight(navRef);
  return /*#__PURE__*/jsxs("nav", {
    className: cx(leftNavCls, location.pathname !== "/" && "atContent"),
    ref: navRef,
    "data-userarea-nav": true,
    children: [/*#__PURE__*/jsx(User, {}), /*#__PURE__*/jsxs("ul", {
      className: listCls,
      children: [/*#__PURE__*/jsxs(Group, {
        children: [/*#__PURE__*/jsx(EntriesLink, {}), /*#__PURE__*/jsx(CoursesLink, {}), /*#__PURE__*/jsx(PortalsLink, {}), /*#__PURE__*/jsx(TeamsLink, {})]
      }), /*#__PURE__*/jsxs(Group, {
        children: [/*#__PURE__*/jsx(SpeechLink, {}), /*#__PURE__*/jsx(FontLink, {}), /*#__PURE__*/jsx(StatusLink, {})]
      })]
    })]
  });
}
function PortalsLink() {
  const user = useUserIdentityState(state => state.user);
  const queryClient = useQueryClient();
  const location = useLocation();
  return /*#__PURE__*/jsx(InternalLink, {
    label: "Fagportaler",
    icon: /*#__PURE__*/jsx(PortalIcon, {
      height: "2.2rem"
    }),
    target: Routes.PORTALS,
    onNavigate: async () => {
      const portals = await queryClient.fetchQuery(getPortalsForUserQuery(user));
      await preloadPortalImages(portals);
    },
    active: location.pathname === Routes.PORTALS
  });
}
function CoursesLink() {
  const user = useUserIdentityState(state => state.user);
  const location = useLocation();
  const queryClient = useQueryClient();
  const notifications = useGetNotificationsQuery({
    enabled: false
  });
  const notificationCount = notifications.data?.filter(it => it.type === "share.course" && isNotificationConsideredRecent(it)).length;
  return /*#__PURE__*/jsx(InternalLink, {
    label: "Mine forløb",
    count: notificationCount,
    icon: /*#__PURE__*/jsx(CoursesIcon, {
      height: "2.2rem"
    }),
    target: Routes.COURSES,
    onNavigate: async () => {
      await Promise.all([prefetchQueryIfNeeded(queryClient, getAllCoursesQuery(user)), prefetchQueryIfNeeded(queryClient, getNotificationsQuery(user))]);
    },
    active: location.pathname === Routes.COURSES
  });
}
function EntriesLink() {
  const user = useUserIdentityState(state => state.user);
  const location = useLocation();
  const queryClient = useQueryClient();
  const timespan = useComponentEntrySelector(state => state.filters.timespan);
  const notifications = useGetNotificationsQuery({
    enabled: false
  });
  const notificationCount = notifications.data?.filter(it => it.type === "share.componentEntry" && isNotificationConsideredRecent(it)).length;
  return /*#__PURE__*/jsx(InternalLink, {
    label: "Besvarelser",
    count: notificationCount,
    icon: /*#__PURE__*/jsx(ResultsIcon, {
      height: "2.2rem"
    }),
    target: Routes.ENTRIES,
    onNavigate: async () => {
      await Promise.all([prefetchQueryIfNeeded(queryClient, getComponentEntriesQuery(user, timespan)), prefetchQueryIfNeeded(queryClient, getNotificationsQuery(user)), prefetchQueryIfNeeded(queryClient, getTeamsQuery(user))]);
    },
    active: location.pathname === Routes.ENTRIES
  });
}
function TeamsLink() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const user = useUserIdentityState(state => state.user);
  return /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsx(InternalLink, {
      label: "Mine hold",
      icon: /*#__PURE__*/jsx(TeamsIcon, {
        height: "2.2rem"
      }),
      target: Routes.TEAMS,
      onNavigate: async () => {
        await prefetchQueryIfNeeded(queryClient, getTeamsQuery(user));
      },
      active: location.pathname === Routes.TEAMS
    })
  });
}
function SpeechLink() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  return /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsx(InternalLink, {
      label: "Indstil oplæsning",
      icon: /*#__PURE__*/jsx(ReadOutLoudIcon, {
        height: "2.2rem"
      }),
      target: Routes.SPEECH,
      onNavigate: async () => {
        await prefetchQueryIfNeeded(queryClient, userPreferencesQuery.queryConfig({
          userId: user?.id ?? null
        }));
      },
      active: location.pathname === Routes.SPEECH
    })
  });
}
function FontLink() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  return /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsx(InternalLink, {
      label: "Indstil skrifttype",
      icon: /*#__PURE__*/jsx(FontIcon, {
        height: "2.2rem"
      }),
      target: Routes.FONT,
      onNavigate: async () => {
        await prefetchQueryIfNeeded(queryClient, userPreferencesQuery.queryConfig({
          userId: user?.id ?? null
        }));
      },
      active: location.pathname === Routes.FONT
    })
  });
}
function StatusLink() {
  const productId = useAppContext(state => state.product?.isbn);
  const queryClient = useQueryClient();
  const location = useLocation();
  const status = useQuery({
    ...getStatusForProductQuery(productId),
    enabled: false
  });
  const statusCount = status.data?.length;
  return /*#__PURE__*/jsx(InternalLink, {
    label: "Teknisk support",
    count: statusCount,
    icon: /*#__PURE__*/jsx(EkgIcon, {
      height: "1.6rem"
    }),
    target: Routes.STATUS,
    onNavigate: async () => {
      await prefetchQueryIfNeeded(queryClient, getStatusForProductQuery(productId));
    },
    active: location.pathname === Routes.STATUS
  });
}

/**
 * Page transitions rely on the navigation to determine the minimum height a
 * given page can have. This is done using nav.clientHeight.. What this means
 * is that the navigation MUST have a integer as its height else rounding
 * problems might cause animation jumps
 */
function useRoundedHeight(navRef) {
  useEffect(() => {
    const updateHeight = throttleToAnimationFrame(() => {
      const nav = navRef.current;
      if (nav) {
        nav.style.setProperty("--nav-rounded-height", null);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        nav.offsetHeight;
        nav.style.setProperty("--nav-rounded-height", `${nav.scrollHeight + 1}px`);
      }
    });
    updateHeight();
    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
    };
  }, [navRef]);
}

export { LeftNav, userAreaNavSelector };
//# sourceMappingURL=root.left-nav.js.map
